"use client";

import { Button, buttonVariants } from "@/components/ui/button";

import {
  Calendar2,
  Call,
  Eye,
  Message2,
  Play,
  Pause,
  AddSquare,
  TickSquare,
  VolumeHigh,
  VolumeMute,
} from "iconsax-react";
import { useRef, useState, memo } from "react";
import RequestMeeting from "../../Schedule/RequestMeeting";
import { Badge } from "@/components/ui/badge";
import getInitials from "@/lib/getInitials";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Link from "next/link";
import ActionToggleFollow from "@/components/customs/Profile/ActionToggleFollow";
import { useRouter } from "next/navigation";
import { SupportedLanguage } from "@/lib/shared";
import { localizedPath } from "@/lib/localePath";
import { useVideoPlayer } from "@/hooks/useVideoPlayer";
import { useNavigateOnTapOnly } from "@/hooks/useNavigateOnTapOnly";
import lang from "./lang.json";

type CardProfilePlayerProps = {
  isFollowed: boolean;
  contentURL: string;
  name: string;
  avatarURL?: string;
  position: string;
  age: number;
  years: number;
  views: number;
  availability: "Available" | "Not Available";
  description: string;
  tags: string[];
  isActive?: boolean;
  userId?: string;
  onScrollDisableChange?: (disabled: boolean) => void;
  isLoggedIn: boolean;
  myId?: string;
  hasJobAd?: boolean;
  jobId?: string;
  isApplied?: boolean;
  locale: SupportedLanguage;
};

export default memo(function CardProfilePlayer({
  isFollowed,
  contentURL,
  name,
  avatarURL,
  position,
  age,
  years,
  views,
  availability,
  description,
  tags,
  isActive = false,
  userId,
  onScrollDisableChange,
  isLoggedIn,
  myId,
  hasJobAd,
  jobId,
  isApplied,
  locale,
}: CardProfilePlayerProps) {
  const MAX_LENGTH_CHAR_DESCRIPTION = 111;
  const router = useRouter();
  const contentRef = useRef<HTMLParagraphElement>(null);
  const [isExpanded, isExpandedSet] = useState(false);

  const {
    videoRef,
    isPlaying,
    showIcon,
    progress,
    isMuted,
    togglePlay,
    handleProgressClick,
    handleMuteToggle,
  } = useVideoPlayer({ src: contentURL, isActive });

  const initials = getInitials(name);

  const profileHref = localizedPath(locale, `/profiles/${userId}`);
  const profileTapAvatar = useNavigateOnTapOnly();
  const profileTapName = useNavigateOnTapOnly();
  const messagesTap = useNavigateOnTapOnly();
  const applyJobTap = useNavigateOnTapOnly();

  // Check if content exceeds character limit and prepare truncated content
  const fullContent = description + " " + tags.join(" ");
  const shouldShowButton = fullContent.length > MAX_LENGTH_CHAR_DESCRIPTION;
  const maxContentLength = MAX_LENGTH_CHAR_DESCRIPTION - " see more".length;
  const truncatedContent = shouldShowButton
    ? fullContent.substring(0, maxContentLength) + "..."
    : fullContent;

  return (
    <div
      id="video-wrapper"
      className="relative h-full min-h-0 overflow-hidden max-lg:flex max-lg:flex-col max-lg:rounded-none lg:flex lg:items-center lg:justify-center lg:rounded-2xl"
    >
      {/* موبايل: بدون aspect-ratio حتى يملأ ارتفاع الشريحة بالكامل (لا قص للأسفل). lg: 9/16 كما كان */}
      <div
        className="relative z-10 h-full w-full min-h-0 flex-1 bg-black rounded-none overflow-hidden lg:aspect-[9/16] lg:flex-none"
      >
        <div className="absolute inset-0 z-10 bg-black">
          <video
            ref={videoRef}
            className={`h-full w-full cursor-pointer object-center transition-opacity duration-300 max-lg:object-contain lg:object-contain ${isActive ? "opacity-100" : "opacity-0"}`}
            muted={isMuted}
            loop
            playsInline
            preload="metadata"
            onClick={() => togglePlay()}
          >
            Your browser does not support the video tag.
          </video>

          {/* Play/Pause Icon Overlay */}
          <div
            className={`absolute z-10 inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-300 ${
              showIcon ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="bg-black/50 rounded-full p-4">
              {isPlaying ? (
                <Play
                  variant="Bold"
                  className="fill-current text-white"
                  size={32}
                />
              ) : (
                <Pause
                  variant="Bold"
                  className="fill-current text-white"
                  size={32}
                />
              )}
            </div>
          </div>

          {/* Mute/Unmute Button */}
          <div className="absolute top-4 right-4 z-20">
            <button
              onClick={handleMuteToggle}
              className="bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
            >
              {isMuted ? (
                <VolumeMute
                  variant="Bold"
                  className="fill-current text-white"
                  size={20}
                />
              ) : (
                <VolumeHigh
                  variant="Bold"
                  className="fill-current text-white"
                  size={20}
                />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div
        className="absolute bottom-0 left-0 w-full h-1.5 bg-black/40 cursor-pointer z-30 hover:h-2 transition-all group"
        onClick={handleProgressClick}
      >
        <div
          className="h-full bg-white relative"
          style={{ width: `${progress}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      {/* Profile info */}
      <div className="absolute z-20 bottom-0 start-0 w-full px-4 pe-16 lg:pe-4 pt-6 pb-4 bg-gradient-to-t from-black/60 to-transparent text-white">
        <div className="pointer-events-auto flex items-center gap-3">
          <div className="relative h-16 w-16 shrink-0 rounded-full border-2 border-white">
            <Link
              href={profileHref}
              onPointerDown={profileTapAvatar.onPointerDown}
              onClick={profileTapAvatar.onClick}
              className="block h-full w-full rounded-full overflow-hidden"
              aria-label={name}
            >
              <Avatar className="w-full h-full rounded-full overflow-hidden">
                <AvatarImage src={avatarURL} className="object-cover" />
                <AvatarFallback className="bg-gray-900">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Link>
            <span className="absolute bottom-0 left-1/2 z-10 translate-y-1/2 -translate-x-1/2">
              <ActionToggleFollow id={userId!} initialIsFollowing={isFollowed}>
                {(handleToggleFollow, isLoading, isFollowing) => (
                  <Button
                    size="icon-xs"
                    variant="secondary"
                    onClick={
                      isLoggedIn
                        ? handleToggleFollow
                        : () =>
                            router.push(
                              localizedPath(
                                locale,
                                `/auth/login?redirect=/?next=follow-user-${userId}`,
                              ),
                            )
                    }
                  >
                    {isLoading ? (
                      "..."
                    ) : isLoggedIn && isFollowing ? (
                      <TickSquare
                        variant="Outline"
                        size={8}
                        className="fill-pink-600"
                      />
                    ) : (
                      <AddSquare
                        variant="Outline"
                        size={8}
                        className="fill-gray-900"
                      />
                    )}
                  </Button>
                )}
              </ActionToggleFollow>
            </span>
          </div>
          <div className="flex flex-col min-w-0">
            <Link
              href={profileHref}
              onPointerDown={profileTapName.onPointerDown}
              onClick={profileTapName.onClick}
              className="min-w-0"
            >
              <h3 className="text-xl font-bold truncate">{name}</h3>
            </Link>
            <p className="text-sm">
              {position} | {age || 1}y
            </p>
            <div className="flex items-center gap-2">
              <Button
                disabled
                size="icon"
                variant="secondary"
                className="text-white bg-white/20 hover:bg-white/30"
              >
                <Call variant="Bold" className="fill-current" size={16} />
              </Button>
              <Link
                href={localizedPath(
                  locale,
                  `/me/conversations?userId=${encodeURIComponent(userId ?? "")}`,
                )}
                onPointerDown={messagesTap.onPointerDown}
                onClick={messagesTap.onClick}
                className={buttonVariants({
                  size: "icon",
                  variant: "secondary",
                  className: "text-white bg-white/20 hover:bg-white/30",
                })}
              >
                <Message2 variant="Bold" className="fill-current" size={16} />
              </Link>
              <RequestMeeting
                locale={locale}
                participantId={userId!}
                triggerClassName="flex-1"
                buttonText="Request Meeting"
                dialogTitle="Request Meeting"
                onOpenChange={(e) => {
                  onScrollDisableChange?.(e);
                  togglePlay(!e);
                }}
                nodeButton={
                  <Button
                    disabled={userId === myId}
                    size="icon"
                    variant="secondary"
                    className=" bg-white/20 hover:bg-white/30 text-white"
                  >
                    <Calendar2
                      variant="Bold"
                      className="fill-current"
                      size={16}
                    />
                  </Button>
                }
              />
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Badge variant="opaque" size="default">
              +{years} Years
            </Badge>

            <Badge variant="opaque" size="default">
              <Eye variant="Bold" className="fill-current" size={12} />
              {views}
            </Badge>

            <Badge variant="default" size="default">
              {availability}
            </Badge>
          </div>
        </div>

        <div className="mt-3"></div>

        <p ref={contentRef} className="text-xs">
          <span>{isExpanded ? fullContent : truncatedContent}</span>
          {shouldShowButton && (
            <Button
              variant="link"
              className="text-white p-0 h-auto inline-block align-baseline ml-1"
              onClick={() => isExpandedSet(!isExpanded)}
            >
              {isExpanded ? "less" : "more"}
            </Button>
          )}
        </p>
        {hasJobAd &&
          jobId &&
          (isApplied ? (
            <span
              className={buttonVariants({
                size: "sm",
                variant: "secondary",
                class: "mt-3 pointer-events-none opacity-70",
              })}
            >
              {lang["applied"][locale]}
            </span>
          ) : (
            <Link
              href={localizedPath(
                locale,
                `apply-job?company=${encodeURIComponent(name)}&jobId=${encodeURIComponent(jobId)}`,
              )}
              onPointerDown={applyJobTap.onPointerDown}
              onClick={applyJobTap.onClick}
              className={buttonVariants({
                size: "sm",
                variant: "secondary",
                class: "mt-3",
              })}
            >
              {lang["apply-job"][locale]}
            </Link>
          ))}
      </div>
    </div>
  );
});
